import { ReactNode, useEffect } from 'react';

import { HelpSidebarSegment } from 'components/help_sidebar/components/HelpSidebarSegment';
import { HelpSidebarContext } from 'components/help_sidebar/context';
import { useSuperState, useWindowDimensions } from 'hooks';
import { Button, Offcanvas } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useApp } from 'app';
import cn from 'classnames';

import 'components/help_sidebar/styles/HelpSidebar.scss';

export type HelpSegment = {
    body: ReactNode;
    title: ReactNode;
};

export type HelpPageContent = {
    segments: HelpSegment[];
    title: ReactNode;
};

export type HelpSidebarContextValue = {
    hide: () => void;
    show: (content: HelpPageContent) => void;
    visible: boolean;
}

export const HelpSidebar = props => {

    const { children } = props;

    const [state, setState, resetState] = useSuperState({
        content: null as HelpPageContent,
        visible: false
    });
    const { content, visible } = state;

    const { isFeatureEnabled } = useApp();
    const { isBelow: isTabletOrMobile } = useWindowDimensions({ target: 1400 });
    const { isBelow: isMobile } = useWindowDimensions({ target: 'sm' });

    const location = useLocation();

    const show = (content: HelpPageContent) => {

        if (!isFeatureEnabled('Admin.General.HelpSidebar')) return console.warn('HelpSidebar feature not enabled.');
        if (!content) return console.error('No content supplied to HelpSidebar.show()');

        setState({ content, visible: true });

    };

    const hide = () => resetState();

    const handleExited = () => setState({ content: null });

    // TODO - Very archaic for now, if the route changes, close the sidebar. 
    //  This would ideally be aware/reactive of "help context" changes instead
    useEffect(() => {

        hide();

    }, [location.pathname]);

    const context = {
        hide,
        show,
        visible
    };

    const className = cn('help-sidebar', { 'w-100': isMobile });

    return (
        <HelpSidebarContext.Provider value={context}>

            {children}

            <Offcanvas
                autoFocus={isTabletOrMobile}
                backdrop={isTabletOrMobile}
                className={className}
                enforceFocus={isTabletOrMobile}
                onExited={handleExited}
                onHide={hide}
                placement={'end'}
                scroll={isTabletOrMobile}
                show={visible}
            >
                <Offcanvas.Header closeButton className={'border-bottom'}>
                    <Offcanvas.Title>
                        {content?.title ?? <FormattedMessage defaultMessage="Help" id="help.1279c64f" />}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className={'d-flex flex-column gap-3'}>
                        {content?.segments?.length ?
                            (
                                <>
                                    {content.segments.map((segment, key) => (
                                        <HelpSidebarSegment key={key} segment={segment} />
                                    ))}
                                </>
                            )
                            :
                            (
                                <p className={'text-muted'}>
                                    <FormattedMessage defaultMessage="No help content is available for this page." id="no_help_content_is_available_for_this_pa.4c46f974" />
                                </p>
                            )
                        }
                    </div>

                    <div className={'border-top mt-3 pt-3 d-flex flex-column gap-1'}>
                        <h6 className={'text-strong fw-semibold mb-1'}>
                            <FormattedMessage defaultMessage="Need more help?" id="need_more_help.9c2fd212" />
                        </h6>
                        <Button
                            className={'d-flex align-items-center gap-1 p-0'}
                            href="https://support.gmetrix.net"
                            rel={'noopener noreferrer'}
                            size={'sm'}
                            target={'_blank'}
                            variant={'link'}
                        >
                            <span>
                                <FormattedMessage defaultMessage="Contact Support" id="contact_support.fa90777b" />
                            </span>
                            <i className="fa-solid fa-external-link"/>
                        </Button>
                    </div>

                </Offcanvas.Body>
            </Offcanvas>
        </HelpSidebarContext.Provider>
    );

};